import {
    ResponseSpeed,
    TournamentRuleset,
    ProblemGenreEnum,
    ProblemContextEnum,
} from '@types';
import {getShortLevelName} from '@utils/elo';

export const SITE_DOMAIN = 'goproblems.com';

export {ROWS_PER_PAGE} from '@goproblems/shared/contracts/pagination';

export const CREATE_GROUP_COST = 500;

export const LEVELS = Array.from({length: 39}, (_, i) => i);

export const TIME_TRIAL_MIN_LEVEL = 0;
export const TIME_TRIAL_MAX_LEVEL = 35;
export const TIME_TRIAL_ALL_LEVELS = 9999;

export const PROBLEM_CONTEXT_LABELS: Record<ProblemContextEnum, string> = {
    [ProblemContextEnum.UNKNOWN]: 'unknown',
    [ProblemContextEnum.SINGLE_PROBLEM]: 'single_problem',
    [ProblemContextEnum.RUSH]: 'rush',
    [ProblemContextEnum.UNTIMED_RUSH]: 'untimed_rush',
    [ProblemContextEnum.TIME_TRIAL]: 'time_trial',
    [ProblemContextEnum.COLLECTION_TEST]: 'collection_test',
    [ProblemContextEnum.MULTIPLAYER]: 'multiplayer',
    [ProblemContextEnum.TRAINING]: 'training',
};

export const TOURNAMENT_DEFAULTS = {
    PRIVATE_TOURNAMENT_COST: 100,
    MAX_PLAYERS: 50,
    SOLVING_ROYALE_BASE_LEVEL: 2,
    SOLVING_ROYALE_MAX_LEVEL: 31,
    SOLVING_ROYALE_STEP: 5,
    SOLVING_ROYALE_THRESHOLD: 2,
    SOLVING_ROYALE_EXPONENT: 0.92,
    SOLVING_ROYALE_PROBLEMS_COUNT: 30,
    SOLVING_ROYALE_ROUND_DURATION: 60,
    SOLVING_ROYALE_PAUSE_BETWEEN_ROUNDS: 10,
    SOLVING_ROYALE_MINIMAL_PLAYERS_COUNT: 2,
    SOLVING_ROYALE_LIVES: 3,
    SOLVING_ROYALE_GOBITS: 10,
};

export const TOURNAMENT_RULESET_CONFIG = {
    [TournamentRuleset.SolvingRoyale]: {
        baseLevel: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_BASE_LEVEL,
        maxLevel: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_MAX_LEVEL,
        step: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_STEP,
        threshold: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_THRESHOLD,
        exponent: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_EXPONENT,
        problemsCount: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_PROBLEMS_COUNT,
        roundDuration: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_ROUND_DURATION,
        pauseBetweenRounds:
            TOURNAMENT_DEFAULTS.SOLVING_ROYALE_PAUSE_BETWEEN_ROUNDS,
        minimalPlayersCount:
            TOURNAMENT_DEFAULTS.SOLVING_ROYALE_MINIMAL_PLAYERS_COUNT,
        lives: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_LIVES,
        gobits: TOURNAMENT_DEFAULTS.SOLVING_ROYALE_GOBITS,
    },
};

// Each response speed totals 650 ms with its speed and animation duration.
// RushTimeLeftCalculator grants the same transition pause per saved result.
export const NEXT_PROBLEM_DELAY = {
    [ResponseSpeed.SLOW]: 0,
    [ResponseSpeed.NORMAL]: 250,
    [ResponseSpeed.FAST]: 500,
    [ResponseSpeed.INSTANT]: 650,
};

export const ANIMATION_DURATION = {
    [ResponseSpeed.SLOW]: 150,
    [ResponseSpeed.NORMAL]: 100,
    [ResponseSpeed.FAST]: 50,
    [ResponseSpeed.INSTANT]: 0,
};

export const NEXT_DISPLAY_NAME = {
    BEGINNER: 'beginner',
    EASY: 'easy',
    MEDIUM: 'medium',
    HARD: 'hard',
    RECOMMENDED: 'recommended',
    PRACTICE: 'practice',
    NEEDING_RATING: 'needing_rating',
    AUTOLEVEL: 'autolevel',
    COLLECTION: 'collection',
    NEWEST: 'newest',
    FRESH: 'fresh',
    CUSTOM: 'custom',
    LEVEL_PROGRESS: 'levelProgress',
    MADE_BY_USER: 'madeByUser',
    SET: 'set',
};

export const PROBLEM_GENRE_OPTIONS = Object.entries(ProblemGenreEnum).map(
    ([, value]) => ({
        label: value,
        value,
    })
);

export const PROBLEM_GENRE_OPTIONS_EXPECT_ELEMENTARY =
    PROBLEM_GENRE_OPTIONS.filter(
        option => option.value !== ProblemGenreEnum.ELEMENTARY
    );

export const DIFFICULTIE_OPTIONS = LEVELS.map(level => ({
    label: getShortLevelName(level),
    value: level,
}));

// Speed solve time limits (in milliseconds)
export const SPEED_SOLVE_FIRST_MOVE_TIME_LIMIT = 5000;
export const SPEED_SOLVE_SUBSEQUENT_MOVE_TIME_LIMIT = 2000;
